import { useState } from 'react'
import { CheckCircle2, Loader2, Smartphone } from 'lucide-react'
import { submitTester, isSupabaseConfigured } from '../lib/supabase'
import { GooglePlayBadge, AppStoreBadge } from './StoreBadges'
import { AppleLogo } from './AppleLogo'

type FormState = {
  full_name: string
  email: string
  platform: 'android' | 'ios' | ''
  website: string // honeypot
}

type Errors = Partial<Record<keyof FormState, string>>

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validate(s: FormState): Errors {
  const e: Errors = {}
  const name = s.full_name.trim()
  if (name.length < 2 || name.length > 80) e.full_name = 'Enter your full name (2–80 characters).'
  if (!EMAIL_RE.test(s.email.trim())) e.email = 'Enter a valid email address.'
  if (s.platform !== 'android' && s.platform !== 'ios') e.platform = 'Pick a platform.'
  return e
}

export function TesterSignup() {
  const [form, setForm] = useState<FormState>({
    full_name: '',
    email: '',
    platform: '',
    website: '',
  })
  const [errors, setErrors] = useState<Errors>({})
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState<{ alreadySignedUp: boolean } | null>(null)
  const [topError, setTopError] = useState<string | null>(null)
  const configured = isSupabaseConfigured()

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTopError(null)

    if (form.website) {
      // honeypot tripped
      setDone({ alreadySignedUp: false })
      return
    }

    const errs = validate(form)
    setErrors(errs)
    if (Object.keys(errs).length > 0) return

    setSubmitting(true)
    const res = await submitTester({
      full_name: form.full_name.trim(),
      email: form.email.trim().toLowerCase(),
      platform: form.platform as 'android' | 'ios',
      user_agent: navigator.userAgent,
      referrer: document.referrer || null,
    })
    setSubmitting(false)

    if (res.ok) {
      setDone({ alreadySignedUp: res.alreadySignedUp })
      try {
        // best-effort analytics hook
        window.dispatchEvent(
          new CustomEvent('tester_signup_submitted', {
            detail: { platform: form.platform },
          }),
        )
      } catch {
        /* noop */
      }
    } else {
      setTopError(res.message)
      try {
        window.dispatchEvent(
          new CustomEvent('tester_signup_failed', {
            detail: { reason: res.reason },
          }),
        )
      } catch {
        /* noop */
      }
    }
  }

  const reset = () => {
    setDone(null)
    setForm({ full_name: '', email: '', platform: '', website: '' })
    setErrors({})
    setTopError(null)
  }

  return (
    <section id="tester" className="mx-auto max-w-3xl px-5 py-16 md:py-24">
      <div className="text-center">
        <span className="chip bg-brand/10 text-brand">Early access</span>
        <h2 className="mt-3">Become an Internal Tester</h2>
        <p className="mx-auto mt-3 max-w-xl text-ink-soft">
          Get early access to new features — Fuzzy Search, Knowledge Base,
          mobile widgets, faster PDFs — before they ship to the Play Store /
          App Store. We&apos;ll add you to the Android internal-testing track
          or the iOS TestFlight group within 24 hours.
        </p>
      </div>

      <div className="mt-8">
        {done ? (
          <SuccessCard alreadySignedUp={done.alreadySignedUp} onReset={reset} />
        ) : (
          <form className="card" onSubmit={onSubmit} noValidate>
            {!configured && (
              <div className="mb-4 rounded-input border border-amber-200 bg-amber-50 p-3 text-sm text-status-warningDark">
                We&apos;re connecting the sign-up form right now. In the
                meantime, please email{' '}
                <a
                  href="mailto:hello@bimauncle.com"
                  className="font-semibold underline"
                >
                  hello@bimauncle.com
                </a>{' '}
                with your name and platform.
              </div>
            )}
            {topError && (
              <div className="mb-4 rounded-input border border-red-200 bg-red-50 p-3 text-sm text-status-errorDark">
                {topError}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id="full_name"
                label="Full name"
                error={errors.full_name}
                value={form.full_name}
                onChange={(v) => setForm({ ...form, full_name: v })}
                autoComplete="name"
                placeholder="Rajesh Kumar"
              />
              <Field
                id="email"
                label="Email"
                type="email"
                error={errors.email}
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                autoComplete="email"
                placeholder="you@example.com"
              />
            </div>

            <fieldset className="mt-5">
              <legend className="text-sm font-semibold">Platform</legend>
              <p className="text-xs text-ink-soft">Where do you want the invite?</p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <PlatformRadio
                  selected={form.platform === 'android'}
                  onClick={() => setForm({ ...form, platform: 'android' })}
                  icon={<Smartphone size={18} />}
                  label="Android"
                  sub="Play Store track"
                />
                <PlatformRadio
                  selected={form.platform === 'ios'}
                  onClick={() => setForm({ ...form, platform: 'ios' })}
                  icon={<AppleLogo size={16} />}
                  label="iOS"
                  sub="TestFlight"
                />
              </div>
              {errors.platform && (
                <p className="mt-2 text-sm text-status-errorDark">{errors.platform}</p>
              )}
            </fieldset>

            {/* honeypot */}
            <div className="hidden" aria-hidden="true">
              <label>
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary mt-6 w-full"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Submitting…
                </>
              ) : (
                'Join Tester Group'
              )}
            </button>

            <p className="mt-4 text-center text-xs text-ink-soft">
              We use your email only to send the tester invite. See our{' '}
              <a href="#privacy" className="font-semibold text-ink underline">
                Privacy Policy
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </section>
  )
}

function Field({
  id,
  label,
  error,
  value,
  onChange,
  type = 'text',
  autoComplete,
  placeholder,
}: {
  id: string
  label: string
  error?: string
  value: string
  onChange: (v: string) => void
  type?: string
  autoComplete?: string
  placeholder?: string
}) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-sm font-semibold">{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-err` : undefined}
        className={`input mt-1.5 ${error ? 'input-error' : ''}`}
      />
      {error && (
        <span id={`${id}-err`} className="mt-1.5 block text-sm text-status-errorDark">
          {error}
        </span>
      )}
    </label>
  )
}

function PlatformRadio({
  selected,
  onClick,
  icon,
  label,
  sub,
}: {
  selected: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  sub: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex items-center gap-3 rounded-input border p-3 text-left transition-colors ${
        selected
          ? 'border-brand bg-brand/8 shadow-focusring'
          : 'border-[var(--color-divider)] bg-white hover:bg-[var(--color-nav)]'
      }`}
    >
      <span
        className={`flex h-9 w-9 items-center justify-center rounded-xl ${
          selected ? 'bg-brand text-white' : 'bg-brand/10 text-brand'
        }`}
      >
        {icon}
      </span>
      <span className="flex-1">
        <span className="block text-sm font-semibold">{label}</span>
        <span className="block text-xs text-ink-soft">{sub}</span>
      </span>
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full border ${
          selected ? 'border-brand bg-brand' : 'border-[var(--color-divider)] bg-white'
        }`}
        aria-hidden
      >
        {selected && <span className="h-2 w-2 rounded-full bg-white" />}
      </span>
    </button>
  )
}

function SuccessCard({
  alreadySignedUp,
  onReset,
}: {
  alreadySignedUp: boolean
  onReset: () => void
}) {
  return (
    <div className="card animate-fade-in text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-status-successDark">
        <CheckCircle2 size={28} />
      </div>
      <h3 className="mt-4">
        {alreadySignedUp ? "You're already on the list." : "You're in! Download below 👇"}
      </h3>
      <p className="mt-2 text-sm text-ink-soft">
        Tap your platform to get the app. We've also emailed you the invite.
      </p>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        <GooglePlayBadge height={52} />
        <AppStoreBadge height={52} />
      </div>
      <p className="mt-3 text-xs text-ink-soft">
        Now live on Google Play and the App Store — install in one tap.
      </p>

      <button
        type="button"
        onClick={onReset}
        className="mt-4 text-sm font-semibold text-brand hover:underline"
      >
        Sign up another device
      </button>
    </div>
  )
}
